/**
 * Shared selectors and formatters for the student dashboard suite.
 *
 * Module: lib/student-helpers
 * Layer:  util (pure functions)
 *
 * Purpose:
 *     Centralises the derived values the four dashboards and the student
 *     pages all need — upcoming/completed session filters, total attendance,
 *     a locale date formatter, and the session-status -> i18n-key map — so
 *     each surface stays small and the logic lives in exactly one place.
 *
 * AI context:
 *     - Entry points called by: every student dashboard + page.
 *     - Depends on: the store interfaces from lib/student-store only.
 *     - Side effects: none (pure functions over readonly inputs).
 *     - Invariants: never mutate inputs; date formatting tolerates an
 *       unparsable string by returning it unchanged.
 *     - Replacement cost: LOW.
 *
 * Threat model:
 *     - No untrusted input; values originate from the authenticated BFF.
 */

import type { TranslationKey } from './i18n';
import type { BookedSession, Enrollment } from './student-store';

/** Map a booked-session status to its i18n label key (data-driven). */
export const SESSION_STATUS_KEY: Readonly<Record<BookedSession['status'], TranslationKey>> = {
  upcoming: 'student.session.statusUpcoming',
  completed: 'student.session.statusCompleted',
  cancelled: 'student.session.statusCancelled',
};

/**
 * Select sessions that are still upcoming.
 *
 * @param sessions - All booked sessions.
 * @returns Only the sessions whose status is `'upcoming'`.
 */
export function upcomingSessions(
  sessions: readonly BookedSession[],
): readonly BookedSession[] {
  return sessions.filter((session) => session.status === 'upcoming');
}

/**
 * Count sessions that have been completed.
 *
 * @param sessions - All booked sessions.
 * @returns The number of completed sessions.
 */
export function completedCount(sessions: readonly BookedSession[]): number {
  return sessions.filter((session) => session.status === 'completed').length;
}

/**
 * Sum attended sessions across every enrollment.
 *
 * @param enrollments - All course enrollments.
 * @returns The total attendance across courses.
 */
export function totalAttendance(enrollments: readonly Enrollment[]): number {
  return enrollments.reduce((sum, enrollment) => sum + enrollment.sessions_attended, 0);
}

/**
 * Format an ISO date string for display.
 *
 * @param iso - An ISO-8601 date string.
 * @returns A locale date string, or the input when it cannot be parsed.
 * @example
 *   formatDate('2026-05-24T10:00:00Z'); // -> '5/24/2026' (locale-dependent)
 */
export function formatDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    // WHY: return the raw value so a malformed date is visible in the UI
    // rather than rendering the misleading "Invalid Date" literal.
    return iso;
  }
  return parsed.toLocaleDateString();
}
